/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef, PostSortRowsParams,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../../common/constants';
import {DeletePopUpComponent} from '../../../../core/components/pop-ups/delete-pop-up/delete-pop-up.component';
import {AutocompleteFilterComponent} from '../../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {StateService} from '../../../../core/state/state.service';
import {IndustryScenario} from '../../../../models/industry-scenario-dto.model';
import {VehicleScenario} from '../../../../models/vehicle-scenario-dto.model';
import {IndustryScenarioService} from '../../../../services/industry-scenario.service';
import {VehicleScenarioService} from '../../../../services/vehicle-scenario.service';
import {
  EditIndustryScenarioPopupComponent
} from './edit-industry-scenario-popup/edit-industry-scenario-popup.component';


@Component({
    selector: 'app-manage-industry-scenario',
    templateUrl: './manage-industry-scenario.component.html',
    styleUrls: ['./manage-industry-scenario.component.scss'],
    standalone: false
})
export class ManageIndustryScenarioComponent implements OnInit {
  showSpinner: boolean;
  noData: boolean;
  pageSize: number;
  scenarioHeaders: ColDef[] = [
    {field: 'id', headerName: 'ID', filter: AutocompleteFilterComponent},
    {field: 'industryScenarioName', headerName: 'Name', filter: AutocompleteFilterComponent},
    {field: 'forecastOrganization', headerName: 'FCORG', filter: AutocompleteFilterComponent},
    {field: 'isDeleteLocked', headerName: 'Delete Lock', filter: AutocompleteFilterComponent},
    {field: 'isEditLocked', headerName: 'Edit Lock', filter: AutocompleteFilterComponent},
    {field: 'isHidden', headerName: 'Hidden', filter: AutocompleteFilterComponent},
    {field: 'createdBy', headerName: 'Created By', filter: AutocompleteFilterComponent},
    {field: 'industryScenarioCategory', headerName: 'Scenario Category', filter: AutocompleteFilterComponent},
    {field: 'industryScenarioType', headerName: 'Scenario Type', filter: AutocompleteFilterComponent},
    {field: 'industryScenarioAssumption', headerName: 'Assumption', filter: AutocompleteFilterComponent}
  ];
  selectedValue: any;
  selectedId: number;
  industryScenarios: IndustryScenario[] = [];
  vehicleScenarios: VehicleScenario[] = [];
  scenarioCategories: string[] = [];

  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };

  postSortRows: (params: PostSortRowsParams) => void = (
    params: PostSortRowsParams<IndustryScenario>,
  ) => {
    // Only post sort rows if the user has a primary FCORG preference
    if (this.stateService.userPreferences?.some(pref => pref.preferenceKey === 'Primary FCORG')) {
      const rowNodes = params.nodes;
      // here we put the Primary FCORG rows on top while preserving the sort order if the user has a primary FCORG preference
      let nextInsertPos = 0;
      for (let i = 0; i < rowNodes.length; i++) {
        const fcorg = rowNodes[i].data ? rowNodes[i].data!.forecastOrganization : undefined;
        if (fcorg === this.stateService.userPreferences.find(pref => pref.preferenceKey === 'Primary FCORG')?.preferenceValue) {
          rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
          nextInsertPos++;
        }
      }
    }
  };


  constructor(public snackBar: FabMessageService,
              private readonly industryScenarioService: IndustryScenarioService,
              private readonly vehicleScenarioService: VehicleScenarioService,
              private readonly stateService: StateService,
              private readonly dialog: MatDialog,
              readonly router: Router) {
  }

  ngOnInit(): void {
    this.selectedValue = null;
    this.loadIndustryScenarios();
    this.setVariables();
  }

  /**
   *  sets initial values for variables on init
   */
  setVariables(): void {
    this.pageSize = 20;
  }

  /**
   * Fetch all industry scenarios from backend endpoint
   */
  loadIndustryScenarios(): void {
    this.showSpinner = true;
    this.industryScenarioService.getAllIndustryScenarios().subscribe(
      data => {
        this.industryScenarios = data;
      },
      err => {
        this.errorHandle(err);
      },
      () => {
        this.showSpinner = false;
        this.noData = (this.industryScenarios.length === 0);
      }
    );
  }

  /**
   * Delete a selected Industry Scenario from DB.
   */
  deleteIndustryScenario(): void {
    this.showSpinner = true;
    const industryScenario: IndustryScenario = this.industryScenarios.find(is => is.id === this.selectedId);
    this.vehicleScenarioService.getVehicleScenarioByIndustryScenarioId(this.selectedId).subscribe(
      data => {
        this.vehicleScenarios = data;
      },
      err => {
        this.errorHandle(err);
        this.showSpinner = false;
      },
      () => {
        if (this.vehicleScenarios.length > 0) {
          this.snackBar.displayError('Cannot delete the selected scenario. ' +
            'It is in use by a Vehicle scenario', DISMISS_MSG);
          this.loadIndustryScenarios();
          this.selectedValue = null;
        } else if (industryScenario.isDeleteLocked === false && this.vehicleScenarios.length === 0) {
          const dialogRef = this.dialog.open(DeletePopUpComponent, {
            data: {
              title: 'Delete',
              valueToDelete: this.selectedValue
            }
          });
          dialogRef.afterClosed().subscribe(res => {
            // received data from dialog-component
            if (res && res.data.deleteValue) {
              this.industryScenarioService.deleteIndustryScenario(this.selectedId).subscribe({
                error: err => {
                  this.errorHandle(err);
                  this.loadIndustryScenarios();
                  this.showSpinner = false;
                },
                complete: () => {
                  this.snackBar.displaySuccess('Successfully removed industry scenario', DISMISS_MSG);
                  this.loadIndustryScenarios();
                  this.selectedValue = null;
                }
              });
            }
          });
        } else {
          this.snackBar.displayError('The selected Industry Scenario is locked and can not be deleted', DISMISS_MSG);
          this.loadIndustryScenarios();
          this.selectedValue = null;
        }
        this.showSpinner = false;
      }
    );
  }

  /**
   * Navigates to edit industry scenario page
   */
  editIndustryScenario(id: number): void {
    const industryScenario = this.industryScenarios.find(is => is.id === id);
    this.dialog.open(EditIndustryScenarioPopupComponent, {
      disableClose: true,
      width: '500px',
      data: industryScenario
    }).afterClosed().subscribe({
      complete: () => {
        this.loadIndustryScenarios();
        this.selectedValue = null;
      }
    });
  }

  /**
   * Event for selecting a scenario
   * @param event the event object
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedValue = (event.data.industryScenarioName);
      this.selectedId = event.data.id;
    } else if (event.data.id === this.selectedId) {
      this.selectedValue = null;
      this.selectedId = null;
    }
  }

  /**
   * display message on error.
   * @param error custom error message.
   */
  errorHandle(error: string): void {
    this.showSpinner = false;
    this.selectedValue = null;
    this.snackBar.displayError(error, DISMISS_MSG);
  }
}
